import wujiFetch from '@/utils/wujiFetch';
import getXyManagePathPrefix from '@utils/getXyManagePathPrefix';
import { ndjsonStreamRequest } from '@/utils/stream';
/** Scan 流式响应 - 消息类型枚举 */
export var ScanChunkType;
(function (ScanChunkType) {
    ScanChunkType["Start"] = "start";
    ScanChunkType["Data"] = "data";
    ScanChunkType["Done"] = "done";
    ScanChunkType["Fatal"] = "fatal";
})(ScanChunkType || (ScanChunkType = {}));
/** Scan 流式响应 - 任务类型枚举 */
export var ScanTaskType;
(function (ScanTaskType) {
    ScanTaskType["Project"] = "project";
    ScanTaskType["Page"] = "page";
    ScanTaskType["AICode"] = "aiCode";
})(ScanTaskType || (ScanTaskType = {}));
class I18nConfigService {
    /** 获取 i18n 配置 */
    async get(projectId) {
        const url = `${getXyManagePathPrefix()}/i18n-config/${projectId}`;
        return await wujiFetch(url);
    }
    /** 更新 i18n 配置 */
    async update(projectId, data) {
        const url = `${getXyManagePathPrefix()}/i18n-config/${projectId}`;
        return await wujiFetch(url, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }
    /** 获取项目翻译词条列表 */
    async getTranslations(projectId) {
        const url = `${getXyManagePathPrefix()}/i18n-translate/${projectId}`;
        return await wujiFetch(url);
    }
    /** 更新翻译 */
    async updateTranslation(projectId, hash, translations, isPlatform) {
        const url = `${getXyManagePathPrefix()}/i18n-translate/${projectId}`;
        const translate = Object.entries(translations).map(([lang, value]) => ({
            lang,
            value,
        }));
        const body = {
            hash,
            translate,
        };
        if (isPlatform !== undefined) {
            body.isPlatform = isPlatform;
        }
        return await wujiFetch(url, {
            method: 'PATCH',
            body: JSON.stringify(body),
        });
    }
    async batchUpdateTranslation(projectId, data) {
        const url = `${getXyManagePathPrefix()}/i18n-translate/${projectId}/batch`;
        return await wujiFetch(url, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }
    /** 调用翻译服务 */
    async translate(projectId, source, langs, sourceLang) {
        const url = `${getXyManagePathPrefix()}/i18n-translate/${projectId}/translate`;
        const response = await wujiFetch(url, {
            method: 'POST',
            body: JSON.stringify({ source, langs, sourceLang }),
        });
        return response.data || response;
    }
    /** 调用批量翻译流式服务（NDJSON 格式） */
    async batchTranslate(projectId, source, langs, sourceLang, callbacks) {
        const url = `${getXyManagePathPrefix()}/i18n-translate/${projectId}/batchTranslate`;
        await ndjsonStreamRequest(url, {
            method: 'POST',
            body: JSON.stringify({ source, langs, sourceLang }),
            query: {
                chunkSize: 20,
            },
            onLine: async (chunk) => {
                switch (chunk.type) {
                    case 'data':
                        await callbacks.onProgress?.(chunk.progress);
                        await callbacks.onData?.(chunk.data, chunk.progress);
                        break;
                    case 'error':
                        await callbacks.onProgress?.(chunk.progress);
                        await callbacks.onError?.({
                            message: chunk.error.message,
                            startIndex: chunk.failedChunk.startIndex,
                            count: chunk.failedChunk.count,
                        });
                        break;
                    case 'done':
                        await callbacks.onComplete?.(chunk.summary);
                        break;
                    case 'fatal':
                        await callbacks.onFatal?.(chunk.error);
                        break;
                }
            },
        });
    }
    /** 创建新语言 */
    async createLang(projectId, langs) {
        const url = `${getXyManagePathPrefix()}/i18n-translate/${projectId}/lang`;
        return await wujiFetch(url, {
            method: 'POST',
            body: JSON.stringify({ langs }),
        });
    }
    /** 删除语言 */
    async deleteLang(projectId, langs) {
        const url = `${getXyManagePathPrefix()}/i18n-translate/${projectId}/lang`;
        return await wujiFetch(url, {
            method: 'DELETE',
            body: JSON.stringify({ langs }),
        });
    }
    /** 扫描项目（流式接口） */
    async scan(projectId, source, callbacks) {
        const url = `${getXyManagePathPrefix()}/i18n/${projectId}/scan`;
        await ndjsonStreamRequest(url, {
            method: 'POST',
            body: JSON.stringify({ source }),
            onLine: async (chunk) => {
                await callbacks.onChunk?.(chunk);
            },
            onEnd: async () => {
                await callbacks.onComplete?.();
            },
        });
    }
    /** 获取平台词条翻译结果 */
    async getPlatformTranslations(projectId) {
        const url = `${getXyManagePathPrefix()}/i18n/${projectId}/platform`;
        return await wujiFetch(url);
    }
}
export const i18nConfigService = new I18nConfigService();
